export type KaraokeProvider = 'youtube' | 'dailymotion' | 'vimeo';

export interface KaraokeEntry {
    id: string; // <- song.id
    video: string; // <- provider video id
    provider?: KaraokeProvider;
    url?: string;
    uid?: string;
    title?: string;
    artist?: string;
    language?: string[];
    sourceUrl?: string;
}

export interface KaraokeMeta {
    provider: KaraokeProvider;
    video: string;
    url?: string;
    uid?: string;
}

let cache: Map<string, KaraokeMeta> | null = null;

function inferProviderFromUid(uid?: string): KaraokeProvider | null {
    if (!uid) return null;
    if (uid.startsWith('youtube:')) return 'youtube';
    if (uid.startsWith('dailymotion:')) return 'dailymotion';
    if (uid.startsWith('vimeo:')) return 'vimeo';
    return null;
}

function inferProviderFromUrl(url?: string): KaraokeProvider | null {
    if (!url) return null;
    const u = url.toLowerCase();
    if (u.includes('youtube.com') || u.includes('youtu.be')) return 'youtube';
    if (u.includes('dailymotion.com')) return 'dailymotion';
    if (u.includes('vimeo.com')) return 'vimeo';
    return null;
}

function buildUrl(provider: KaraokeProvider, video: string): string {
    switch (provider) {
        case 'youtube':
            return `https://www.youtube.com/watch?v=${video}`;
        case 'dailymotion':
            return `https://www.dailymotion.com/video/${video}`;
        case 'vimeo':
            return `https://vimeo.com/${video}`;
    }
}

export async function getKaraokeMap(): Promise<Map<string, KaraokeMeta>> {
    if (cache) return cache;

    try {
        const res = await fetch('/karaoke/index.json', { cache: 'no-store' });
        if (!res.ok) {
            cache = new Map();
            return cache;
        }

        const data: KaraokeEntry[] = await res.json();

        cache = new Map(
            (data || [])
                .filter((e) => !!e?.id && !!e?.video)
                .map((e) => {
                    const provider =
                        e.provider ?? inferProviderFromUid(e.uid) ?? inferProviderFromUrl(e.url) ?? ('youtube' as const);
                    const url = e.url ?? buildUrl(provider, e.video);
                    const uid = e.uid ?? `${provider}:${e.video}`;
                    return [e.id, { provider, video: e.video, url, uid }];
                }),
        );

        return cache;
    } catch {
        cache = new Map();
        return cache;
    }
}